
import { System } from './../System';
import { YearService } from './../year/year.service';
import { Profile } from './../profile-table/profile.model';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Component, OnInit, Inject, EventEmitter, OnDestroy } from '@angular/core';
import { ProfileService } from '../profile-table/profile.service';
import { Subscription } from 'rxjs';
import { DomSanitizer } from '@angular/platform-browser';
import { LoginService } from '../../shared-modules/login/login.service';

/**
 * This is a dialog that is used whenever you have a persom viewing the profiles.
 *
 * MAT_DIALOG_DATA:
 *  profiles: a list of profiles in case you already have them
 *  courseSSEC_ID: the course' SSEC_ID for fetching profiles
 *  courseName: the name of the course that will be displayed
 *  courseSection: the courseSection of the course that will be displayed
 *  teacherId: the ID of the teacher for fetching profiles.
 *  teacherName:  the name of the teacher that is displayed.
 *  showTeacherProfileLink: Determines if the course viewer should show a teacher profile link.
 *  teacherProfileClickHandler: this is called whenever the teacher profile link is clicked
 *
 * teacherProfileClick: this event emitter fires when the teacher profile link is clicked
 *
 * NOTE: this will default to using the course if it's available.
 * NOTE: either subscribe to the teacherProfileClick or provide a teacherProfileClickHandler. DO NOT DO BOTH.
 * Supply EITHER teacherID or courseSSEC_ID, not both.
 */
@Component({
    selector: 'profile-viewer',
    templateUrl: './profile-viewer-dialog.component.html',
    styleUrls: [
        './profile-viewer-dialog.component.css'
    ]
})
export class ProfileViewerDialogComponent implements OnInit, OnDestroy {

    // Injected Properties
    courseSSEC_ID: number;
    courseName: string;
    courseSection: string;
    teacherId: number;
    teacherName: string;
    showTeacherProfileLink = false;
    staffPictureBase64: string;
    teacherProfileClickHandler: Function;

    // Local Properties
    profiles: Profile[] = [];
    useCourse: boolean;
    school: string;
    expandEmitter = new EventEmitter<boolean>();
    subscriptions: Subscription[] = [];
    teacherProfileClick = new EventEmitter<any>();
    V10: boolean;

    // Class Functions
    constructor (
        private dialogBox: MatDialogRef<ProfileViewerDialogComponent>,
        @Inject(MAT_DIALOG_DATA) private data: any,
        private profileService: ProfileService,
        private yearService: YearService,
        private sanitizer: DomSanitizer,
        private loginService: LoginService
    ) { }

    // Event Functions
    ngOnInit() {
        // PRE: injected data containing either a course, a teacherId, or a list of profiles.
        //      It should also have a teacherName, but it's not required.
        // POST: initializes the data
        //Setup application identifier
        this.V10 = this.loginService.LoginSettings.V10;
        // Obtaining injected properties
        this.profiles = this.data.profiles;
        this.teacherName = this.data.teacherName;
        this.courseSSEC_ID = this.data.courseSSEC_ID;
        this.courseName = this.data.courseName;
        this.courseSection = this.data.courseSection;
        this.teacherId = this.data.teacherId;
        this.school = this.data.school;
        this.showTeacherProfileLink = this.data.showTeacherProfileLink;
        this.staffPictureBase64 = this.data.staffPictureBase64;
        this.teacherProfileClickHandler = this.data.teacherProfileClickHandler;

        // Setting local properties
        this.useCourse = (this.courseSSEC_ID != null);
        if (!this.school) {
            this.school = this.yearService.defaultYear.SchoolName;
        }
        if (!this.profiles) {
            if (this.useCourse) {
                this.subscriptions.push(
                    this.profileService.getProfileContent(this.courseSSEC_ID, true).subscribe(profiles => {
                        this.profiles = profiles;
                        for (let i = 0; i < this.profiles.length; i++) {
                            this.profiles[i] = System.convert<Profile>(this.profiles[i], new Profile());
                        }
                    })
                );
            } else {
                this.subscriptions.push(
                    this.profileService.getProfileContent(this.teacherId, false).subscribe(profiles => {
                        this.profiles = profiles;
                        for (let i = 0; i < this.profiles.length; i++) {
                            this.profiles[i] = System.convert<Profile>(this.profiles[i], new Profile());
                        }
                    })
                );
            }
        }
        // Since the Teacher Profile link in Course Preview only shows when Teacher has any profile. 
        this.showTeacherProfileLinkWhenTeacherHasProfile();

        this.subscriptions.push(
            this.teacherProfileClick.subscribe(() => {
                this.teacherProfileClickHandler();
            })
        );
    }

    close() {
        // PRE: the close button is clicked
        // POST: closes the dialog
        this.dialogBox.close();
    }

    ngOnDestroy() {
        // POST: unsubscribes to all subscriptions
        this.subscriptions.forEach(subscription => {
            subscription.unsubscribe();
        });
    }

    // Methods
    resourceSrc() {
        // POST: returns a trusted the staff picture image src
		return this.sanitizer.bypassSecurityTrustResourceUrl(
			'data: image/jpeg;' +
			'base64,' + this.staffPictureBase64
		);
    }

    private showTeacherProfileLinkWhenTeacherHasProfile() {
        // PRE: Check Teacher has profile
        // POST: Set showTeacherProfileLink = false if there is no profile for teacher 
        if (!this.V10 && this.useCourse && this.showTeacherProfileLink) {
            this.subscriptions.push(
                this.profileService.getProfileContent(this.teacherId, false).subscribe(profiles => {
                    if (!profiles || profiles.length == 0) {
                        this.showTeacherProfileLink = false;
                    }
                })
            );
        }
    }
}